
import { useCallback, useEffect, useReducer, useRef } from "react";

interface DeckNavState {
  index: number;
  fragment: number;
  direction: 1 | -1;
}

type DeckNavAction =
  | { type: "next"; fragmentCounts: number[] }
  | { type: "prev"; fragmentCounts: number[] }
  | { type: "goto"; index: number; fragmentCounts: number[] };

export function deckNavReducer(
  state: DeckNavState,
  action: DeckNavAction,
): DeckNavState {
  const { fragmentCounts } = action;
  const last = fragmentCounts.length - 1;

  switch (action.type) {
    case "next": {
      if (state.index < last) {
        const index = state.index + 1;
        return { index, fragment: fragmentCounts[index] ?? 0, direction: 1 };
      }
      return state;
    }
    case "prev": {
      if (state.index > 0) {
        const index = state.index - 1;
        return {
          index,
          fragment: fragmentCounts[index] ?? 0,
          direction: -1,
        };
      }
      return state;
    }
    case "goto": {
      const index = Math.min(Math.max(action.index, 0), last);
      if (index === state.index) {
        return state;
      }
      return {
        index,
        fragment: fragmentCounts[index] ?? 0,
        direction: index > state.index ? 1 : -1,
      };
    }
  }
}

const WHEEL_THRESHOLD = 90;
const WHEEL_LOCKOUT_MS = 700;
const WHEEL_DECAY_MS = 300;
const SWIPE_THRESHOLD = 60;

interface UseDeckNavigationOptions {
  slideIds: string[];
  fragmentCounts: number[];
  enabled: boolean;
  onEscape: () => void;
  onToggleNotes: () => void;
}

export interface DeckNavigation {
  index: number;
  fragment: number;
  direction: 1 | -1;
  count: number;
  next: () => void;
  prev: () => void;
  goto: (index: number) => void;
}

export function useDeckNavigation({
  slideIds,
  fragmentCounts,
  enabled,
  onEscape,
  onToggleNotes,
}: UseDeckNavigationOptions): DeckNavigation {
  const [state, dispatch] = useReducer(deckNavReducer, {
    index: 0,
    fragment: 0,
    direction: 1,
  });

  const countsRef = useRef(fragmentCounts);
  const slideIdsRef = useRef(slideIds);
  const enabledRef = useRef(enabled);
  const handlersRef = useRef({ onEscape, onToggleNotes });

  useEffect(() => {
    countsRef.current = fragmentCounts;
    slideIdsRef.current = slideIds;
    enabledRef.current = enabled;
    handlersRef.current = { onEscape, onToggleNotes };
  });

  const next = useCallback(
    () => dispatch({ type: "next", fragmentCounts: countsRef.current }),
    [],
  );
  const prev = useCallback(
    () => dispatch({ type: "prev", fragmentCounts: countsRef.current }),
    [],
  );
  const goto = useCallback(
    (index: number) =>
      dispatch({ type: "goto", index, fragmentCounts: countsRef.current }),
    [],
  );

  useEffect(() => {
    function syncFromHash() {
      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (!hash) return;
      const target = slideIdsRef.current.indexOf(hash);
      if (target >= 0) {
        dispatch({ type: "goto", index: target, fragmentCounts: countsRef.current });
      }
    }
    // replaceState no dispara hashchange, así que el sync no se retroalimenta.
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  useEffect(() => {
    const id = slideIdsRef.current[state.index];
    if (!id) return;
    history.replaceState(null, "", `#${id}`);
  }, [state.index]);

  useEffect(() => {
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      );
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      if (event.key === "Escape") {
        event.preventDefault();
        handlersRef.current.onEscape();
        return;
      }
      // Antes del chequeo de `enabled`: con las notas abiertas la navegación queda
      // deshabilitada, y N tiene que poder cerrarlas.
      if (event.key === "n" || event.key === "N") {
        handlersRef.current.onToggleNotes();
        return;
      }
      if (!enabledRef.current) return;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ":
        case "Enter":
          event.preventDefault();
          next();
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
          event.preventDefault();
          prev();
          break;
        case "Home":
          event.preventDefault();
          goto(0);
          break;
        case "End":
          event.preventDefault();
          goto(countsRef.current.length - 1);
          break;
      }
    }

    let wheelAcc = 0;
    let lastWheelAt = 0;
    let lastNavAt = 0;

    function onWheel(event: WheelEvent) {
      if (!enabledRef.current) return;
      const now = performance.now();
      if (now - lastWheelAt > WHEEL_DECAY_MS) {
        wheelAcc = 0;
      }
      lastWheelAt = now;
      if (now - lastNavAt < WHEEL_LOCKOUT_MS) return;
      wheelAcc += event.deltaY;
      if (Math.abs(wheelAcc) < WHEEL_THRESHOLD) return;
      lastNavAt = now;
      if (wheelAcc > 0) {
        next();
      } else {
        prev();
      }
      wheelAcc = 0;
    }

    let touchStartX = 0;
    let touchStartY = 0;

    function onTouchStart(event: TouchEvent) {
      const touch = event.touches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }

    function onTouchEnd(event: TouchEvent) {
      if (!enabledRef.current) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
      if (Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
      if (deltaX < 0) {
        next();
      } else {
        prev();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [next, prev, goto]);

  return {
    index: state.index,
    fragment: state.fragment,
    direction: state.direction,
    count: slideIds.length,
    next,
    prev,
    goto,
  };
}
